import { currentUrl, useMatchSmallViewport } from '@avensia/excite';
import { SEARCH_PARAMS_SORT_BY } from '@avensia/excite-esales';
import { createSlideHeightAnimationComponent } from '@avensia/excite-ui';
import { styled } from '@glitz/react';
import { bareListDecorator } from 'webapp/decorators/list';
import { createMinLargeViewportDecorator } from 'webapp/viewport';
import * as React from 'react';
import { white } from 'webapp/shared/sharedTheme';
import { SortMobile } from './SortMobile';
import { getSortOptionTestIdProps } from 'webapp/shared/test-ids';

type PropType = {
  sortOptions: NordicNest.Api.SelectOptionApiModel[];
  onChangeSort: (value: string, text: string) => void;
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  refList?: React.RefObject<HTMLLIElement | null>[];
};

export function Sort({ sortOptions, onChangeSort, setIsOpen, isOpen, refList }: PropType) {
  const isSmallViewport = useMatchSmallViewport();
  const defaultSortOptions = currentUrl().searchParams.get(SEARCH_PARAMS_SORT_BY) || 'relevance';
  const [value, setValue] = React.useState(defaultSortOptions);

  return isSmallViewport ? (
    <SortMobile options={sortOptions} isOpen={isOpen} onChangeSort={onChangeSort} close={() => setIsOpen(false)} />
  ) : (
    <>
      <SlowerSlideHeightAnimation visible={isOpen}>
        <List role="listbox">
          {sortOptions.map((option, index) => {
            return (
              <Option
                ref={refList?.[index]}
                tabIndex={-1}
                role="option"
                key={option.value}
                aria-selected={value === option.value ? 'true' : 'false'}
                css={styled(value === option.value && selectValueDecorator)}
                onKeyDown={e => {
                  if (e.code === 'Enter') {
                    setValue(option.value);
                    onChangeSort(option.value, option.text);
                    setIsOpen(false);
                  }
                }}
                onClick={() => {
                  setValue(option.value);
                  onChangeSort(option.value, option.text);
                  setIsOpen(false);
                }}
                {...getSortOptionTestIdProps(option)}
              >
                {option.text}
              </Option>
            );
          })}
        </List>
      </SlowerSlideHeightAnimation>
    </>
  );
}

const SlowerSlideHeightAnimation = createSlideHeightAnimationComponent(
  styled.div({
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: t => t.sharedSpacing.xs,
  }),
  { duration: 300 },
);

const selectValueDecorator = styled({
  fontWeight: 'bold',
});

const List = styled.ul(
  bareListDecorator,
  createMinLargeViewportDecorator({
    boxShadow: 'rgba(0, 0, 0, 0.1) 10px 10px 40px 0px',
  }),
  {
    padding: { x: 0, y: t => t.sharedSpacing.m },
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: white,
    zIndex: 1,
    position: 'relative',
  },
);

const Option = styled.li(
  createMinLargeViewportDecorator({
    padding: { x: t => t.sharedSpacing.l, y: t => t.sharedSpacing.xs },
  }),
  {
    fontSize: t => t.fontSize.xxs,
    cursor: 'pointer',
    ':focus-visible': { backgroundColor: t => t.monochrome.color },
  },
);
